import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, BookOpen, Users, Shield, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * RoleCardsSection — "Pick your role" jump-off panel.
 * ────────────────────────────────────────────────────────────────
 * Replaces the long "How it works" narrative with a single
 * decision-tree row: four role cards, one step each, one CTA each.
 * Designed for the new 5-second-comprehension hero (Feb 2026).
 *
 * Cards (LTR order; flipped automatically by RTL via flex-direction):
 *   • Student   → /register?role=student
 *   • Teacher   → /register?role=teacher
 *   • Parent    → /register?role=parent
 *   • Admin     → /login        (admin invites only — no public signup)
 *
 * Visual contract:
 *   • blue + green + white palette (CEO directive)
 *   • each card has icon + role name + 1-line step + small CTA arrow
 *   • cards lift on hover (transform + ring tightens)
 *   • mobile: stacks 1-col, tablet 2-col, desktop 4-col
 */
const ROLES = [
  {
    key: 'student',
    icon: GraduationCap,
    accent: 'indigo',
    path: '/register?role=student',
    title:    { ar: 'للطلاب',                 en: 'For Students' },
    step:     { ar: 'اختر معلمك واحجز درسك في دقائق', en: 'Pick a tutor & book your first lesson in minutes' },
    cta:      { ar: 'سجّل كطالب',             en: 'Sign up as student' },
  },
  {
    key: 'teacher',
    icon: BookOpen,
    accent: 'emerald',
    path: '/register?role=teacher',
    title:    { ar: 'للمعلمين',               en: 'For Tutors' },
    step:     { ar: 'انضم كمعلم متحقَّق وابدأ بكسب الدخل', en: 'Get verified, build your profile, start earning' },
    cta:      { ar: 'انضم كمعلم',             en: 'Become a tutor' },
  },
  {
    key: 'parent',
    icon: Users,
    accent: 'sky',
    path: '/register?role=parent',
    title:    { ar: 'لأولياء الأمور',         en: 'For Parents' },
    step:     { ar: 'تابع تقدّم أبنائك ودروسهم في مكان واحد', en: 'Track lessons, progress, and safety in one place' },
    cta:      { ar: 'سجّل كولي أمر',           en: 'Sign up as parent' },
  },
  {
    key: 'admin',
    icon: Shield,
    accent: 'amber',
    path: '/login',
    title:    { ar: 'للمسؤولين',              en: 'For Admins' },
    step:     { ar: 'إدارة المنصة بصلاحيات حصرية', en: 'Manage the platform with elevated access' },
    cta:      { ar: 'تسجيل دخول الإدارة',     en: 'Admin login' },
  },
];

const ACCENT_STYLES = {
  indigo:  { ring: 'hover:ring-indigo-400/60',  iconBg: 'bg-indigo-500/10  text-indigo-600  dark:text-indigo-300',  cta: 'text-indigo-600 dark:text-indigo-300' },
  emerald: { ring: 'hover:ring-emerald-400/60', iconBg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300', cta: 'text-emerald-600 dark:text-emerald-300' },
  sky:     { ring: 'hover:ring-sky-400/60',     iconBg: 'bg-sky-500/10     text-sky-600     dark:text-sky-300',     cta: 'text-sky-600 dark:text-sky-300' },
  amber:   { ring: 'hover:ring-amber-400/60',   iconBg: 'bg-amber-500/10   text-amber-600   dark:text-amber-300',   cta: 'text-amber-600 dark:text-amber-300' },
};

export default function RoleCardsSection({ isDark }) {
  const navigate = useNavigate();
  const { language, localize } = useLanguage();
  const isAr = language === 'ar';

  return (
    <section
      id="role-cards"
      className={`px-4 sm:px-6 py-10 sm:py-14 ${isDark ? 'bg-[var(--bg-primary)]' : 'bg-slate-50'}`}
      data-testid="role-cards-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 sm:mb-10">
          <p className="text-[10.5px] tracking-[0.24em] font-bold uppercase text-indigo-500 mb-2">
            {localize({ ar: 'كيف تعمل المنصة', en: 'How it works' })}
          </p>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {localize({ ar: 'اختر دورك وابدأ في 30 ثانية', en: 'Pick your role · Get started in 30 seconds' })}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {ROLES.map((role, idx) => {
            const Icon = role.icon;
            const style = ACCENT_STYLES[role.accent];
            return (
              <motion.button
                key={role.key}
                type="button"
                onClick={() => navigate(role.path)}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -3 }}
                className={`group relative rounded-2xl text-start p-5 sm:p-6 transition-shadow ring-1 ${isDark ? 'bg-slate-900/60 ring-white/10' : 'bg-white ring-slate-200/80'} ${style.ring} shadow-sm hover:shadow-lg`}
                data-testid={`role-card-${role.key}`}
              >
                <span className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${style.iconBg}`}>
                  <Icon className="w-6 h-6" strokeWidth={2.2} />
                </span>
                <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {localize(role.title)}
                </h3>
                <p className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {localize(role.step)}
                </p>
                <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${style.cta}`}>
                  {localize(role.cta)}
                  <ArrowRight className={`w-3.5 h-3.5 transition-transform group-hover:translate-x-1 ${isAr ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
